// The 47 Japanese prefectures, keyed by ISO 3166-2:JP suffix order so the
// Cloudflare-geo -> prefecture mapping (apps/api) can index straight into
// this list. "miyagi"/"tokyo"/"other" match the original 3-option seed
// (0002_seed_default_form.sql) so existing submission_answers stay valid.
export interface PrefectureOption {
  value: string;
  label: string;
  isoCode: string; // "JP-13" etc, as reported by Cloudflare's cf.regionCode
}

export const PREFECTURES: PrefectureOption[] = [
  { value: "hokkaido", label: "北海道", isoCode: "JP-01" },
  { value: "aomori", label: "青森県", isoCode: "JP-02" },
  { value: "iwate", label: "岩手県", isoCode: "JP-03" },
  { value: "miyagi", label: "宮城県", isoCode: "JP-04" },
  { value: "akita", label: "秋田県", isoCode: "JP-05" },
  { value: "yamagata", label: "山形県", isoCode: "JP-06" },
  { value: "fukushima", label: "福島県", isoCode: "JP-07" },
  { value: "ibaraki", label: "茨城県", isoCode: "JP-08" },
  { value: "tochigi", label: "栃木県", isoCode: "JP-09" },
  { value: "gunma", label: "群馬県", isoCode: "JP-10" },
  { value: "saitama", label: "埼玉県", isoCode: "JP-11" },
  { value: "chiba", label: "千葉県", isoCode: "JP-12" },
  { value: "tokyo", label: "東京都", isoCode: "JP-13" },
  { value: "kanagawa", label: "神奈川県", isoCode: "JP-14" },
  { value: "niigata", label: "新潟県", isoCode: "JP-15" },
  { value: "toyama", label: "富山県", isoCode: "JP-16" },
  { value: "ishikawa", label: "石川県", isoCode: "JP-17" },
  { value: "fukui", label: "福井県", isoCode: "JP-18" },
  { value: "yamanashi", label: "山梨県", isoCode: "JP-19" },
  { value: "nagano", label: "長野県", isoCode: "JP-20" },
  { value: "gifu", label: "岐阜県", isoCode: "JP-21" },
  { value: "shizuoka", label: "静岡県", isoCode: "JP-22" },
  { value: "aichi", label: "愛知県", isoCode: "JP-23" },
  { value: "mie", label: "三重県", isoCode: "JP-24" },
  { value: "shiga", label: "滋賀県", isoCode: "JP-25" },
  { value: "kyoto", label: "京都府", isoCode: "JP-26" },
  { value: "osaka", label: "大阪府", isoCode: "JP-27" },
  { value: "hyogo", label: "兵庫県", isoCode: "JP-28" },
  { value: "nara", label: "奈良県", isoCode: "JP-29" },
  { value: "wakayama", label: "和歌山県", isoCode: "JP-30" },
  { value: "tottori", label: "鳥取県", isoCode: "JP-31" },
  { value: "shimane", label: "島根県", isoCode: "JP-32" },
  { value: "okayama", label: "岡山県", isoCode: "JP-33" },
  { value: "hiroshima", label: "広島県", isoCode: "JP-34" },
  { value: "yamaguchi", label: "山口県", isoCode: "JP-35" },
  { value: "tokushima", label: "徳島県", isoCode: "JP-36" },
  { value: "kagawa", label: "香川県", isoCode: "JP-37" },
  { value: "ehime", label: "愛媛県", isoCode: "JP-38" },
  { value: "kochi", label: "高知県", isoCode: "JP-39" },
  { value: "fukuoka", label: "福岡県", isoCode: "JP-40" },
  { value: "saga", label: "佐賀県", isoCode: "JP-41" },
  { value: "nagasaki", label: "長崎県", isoCode: "JP-42" },
  { value: "kumamoto", label: "熊本県", isoCode: "JP-43" },
  { value: "oita", label: "大分県", isoCode: "JP-44" },
  { value: "miyazaki", label: "宮崎県", isoCode: "JP-45" },
  { value: "kagoshima", label: "鹿児島県", isoCode: "JP-46" },
  { value: "okinawa", label: "沖縄県", isoCode: "JP-47" },
];

// Kept as a valid choice (matches the original seed) for auto-detection
// failures, VPNs, and overseas access - see ARCHITECTURE.md#region-auto-detection.
export const REGION_OTHER = { value: "other", label: "その他（海外・不明）" };

export const REGION_OPTIONS = [...PREFECTURES, REGION_OTHER];

const ISO_TO_PREFECTURE = new Map(PREFECTURES.map((p) => [p.isoCode, p.value]));

// Cloudflare's cf.regionCode for Japan follows ISO 3166-2:JP (e.g. "JP-13"
// for Tokyo); some edge cases report just the numeric/short suffix, so both
// forms are checked. Returns null (never a guess) when it can't be mapped
// confidently - callers must fall back to manual selection.
export function mapCloudflareRegionCodeToPrefecture(regionCode: string | null | undefined): string | null {
  if (!regionCode) return null;
  const normalized = regionCode.toUpperCase();
  if (ISO_TO_PREFECTURE.has(normalized)) return ISO_TO_PREFECTURE.get(normalized)!;
  const withPrefix = normalized.startsWith("JP-") ? normalized : `JP-${normalized.padStart(2, "0")}`;
  return ISO_TO_PREFECTURE.get(withPrefix) ?? null;
}

export function prefectureLabel(value: string | null | undefined): string {
  if (!value) return "不明";
  if (value === REGION_OTHER.value) return REGION_OTHER.label;
  return PREFECTURES.find((p) => p.value === value)?.label ?? value;
}
